import React, { useCallback, useMemo } from 'react';
import Immutable from 'immutable';

import useAIVoiceSearch from 'js/src/components/app/VoiceSelectionModal/useAIVoiceSearch';
import { SecondaryButton } from 'js/src/components/common/ui/Button';
import type { DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import type { AIVoiceSortValue } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-slice';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';

type SortSelectData = { value: AIVoiceSortValue };
type SortItem = { id: AIVoiceSortValue; text: string; onSelectData: SortSelectData };

const SORT_OPTIONS: Immutable.List<DropdownItemProp<SortSelectData>> = Immutable.List([
    ImmutableFromJS<SortItem>({
        id: 'trending',
        text: 'Trending',
        onSelectData: { value: 'trending' },
    }),
    ImmutableFromJS<SortItem>({ id: 'a-z', text: 'A to Z', onSelectData: { value: 'a-z' } }),
    ImmutableFromJS<SortItem>({ id: 'z-a', text: 'Z to A', onSelectData: { value: 'z-a' } }),
    ImmutableFromJS<SortItem>({
        id: 'default',
        text: 'Default',
        onSelectData: { value: 'default' },
    }),
]);

const SortToggle = React.memo(function SortToggle({
    text,
    onClick,
    ref,
}: {
    text: string;
    onClick: VoidFunction;
    ref?: React.Ref<HTMLButtonElement>;
}) {
    return <SecondaryButton ref={ref} text={text} iconLeft="list_arrow" onClick={onClick} />;
});

interface AIVoiceSortProps {
    className?: string;
}

const AIVoiceSort: React.FC<AIVoiceSortProps> = ({ className }) => {
    const { selectedSort, onChangeSort } = useAIVoiceSearch();

    const toggleProps = useMemo(
        () =>
            Immutable.fromJS({
                text:
                    SORT_OPTIONS.find((opt) => opt.get('id') === selectedSort)?.get('text') ??
                    'Sort',
            }),
        [selectedSort],
    );

    const menuProps = useMemo(() => Immutable.fromJS({ align: 'end' }), []);

    const handleSelect = useCallback(
        (onSelectData: ImmutableMap<SortSelectData>) => {
            onChangeSort(onSelectData.get('value'));
        },
        [onChangeSort],
    );

    return (
        <div className={className}>
            <Dropdown<SortSelectData>
                ToggleComponent={SortToggle}
                toggleProps={toggleProps}
                itemPropsList={SORT_OPTIONS}
                onSelect={handleSelect}
                menuProps={menuProps}
            />
        </div>
    );
};

export default AIVoiceSort;
